// _shared/hooks/useSponsors.js
import { useState, useEffect, useRef } from 'react';
import { sponsorsService } from '../../_services/sponsorsService';

export const useSponsors = (slug, initialSponsors = [], isEditable = false) => {
  const [sponsors, setSponsors] = useState(
    Array.isArray(initialSponsors) ? initialSponsors : []
  );
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // ✅ REF para controlar si ya se cargaron los datos
  const hasFetched = useRef(false);

  // ✅ SOLO cargar si NO hay initialSponsors y NO se ha cargado antes
  useEffect(() => {
    if (!slug) return;
    if (hasFetched.current) return; // ← EVITA DOBLE CARGA
    if (initialSponsors && initialSponsors.length > 0) return;

    const fetchSponsors = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await sponsorsService.getSponsors(slug);
        setSponsors(data);
        hasFetched.current = true;
      } catch (error) {
        console.error('Error al cargar patrocinadores:', error);
        setError('Error al cargar los patrocinadores');
      } finally {
        setLoading(false);
      }
    };

    fetchSponsors();
  }, [slug, initialSponsors]); // ← Dependencias minimizadas

  // ✅ Sincronizar con initialSponsors sin causar bucles
  useEffect(() => {
    if (initialSponsors && Array.isArray(initialSponsors) && initialSponsors.length > 0) {
      setSponsors(initialSponsors);
      hasFetched.current = true;
    }
  }, [initialSponsors]);


  /**
   * Agregar patrocinadores
   */
  const handleAddSponsors = async (files) => {
    if (!slug) {
      alert("Error: El slug no está definido.");
      return false;
    }

    if (!files || files.length === 0) return false;

    // Validar archivos
    const validation = sponsorsService.validateFiles(files);
    if (validation.hasInvalid) {
      alert(validation.message);
      // Continuar solo con los válidos
      if (validation.valid.length === 0) return false;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await sponsorsService.addSponsors(slug, validation.valid);
      const updatedSponsors = response.sponsors;
      setSponsors(updatedSponsors);
      
      alert("¡Patrocinadores agregados correctamente!");
      return true;
    } catch (error) {
      console.error("Error al subir las imágenes:", error);
      setError(error.response?.data?.message || "No se pudieron subir las imágenes.");
      alert(error.response?.data?.message || "No se pudieron subir las imágenes.");
      return false;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Eliminar patrocinador
   */
  const handleDeleteSponsor = async (sponsorUrl) => {
    if (!window.confirm(`¿Estás seguro de que quieres eliminar este patrocinador?`)) {
      return false;
    }

    if (!slug) {
      alert("Error: El slug no está definido.");
      return false;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await sponsorsService.deleteSponsor(slug, sponsorUrl);

      if (response && response.sponsors) {
        const updatedSponsors = response.sponsors;
        setSponsors(updatedSponsors);
        alert("Patrocinador eliminado correctamente");
        return true;
      } else {
        throw new Error("Respuesta inválida del servidor");
      }
    } catch (error) {
      console.error("Error al eliminar patrocinador:", error);
      setError(error.response?.data?.message || "No se pudo eliminar el patrocinador");
      alert(error.response?.data?.message || "No se pudo eliminar el patrocinador");
      return false;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Alternar modo edición
   */
  const toggleEditing = () => {
    setIsEditing(!isEditing);
  };

  /**
   * Obtener array continuo para animación
   */
  const getContinuousSponsors = () => {
    return sponsorsService.getContinuousSponsors(sponsors);
  };

  /**
   * Obtener placeholder
   */
  const getDefaultSponsor = () => {
    return sponsorsService.getDefaultSponsor();
  };

  return {
    // Estado
    sponsors,
    isEditing,
    loading,
    error,

    // Setters
    setSponsors,
    setIsEditing,

    // Handlers
    handleAddSponsors,
    handleDeleteSponsor,
    toggleEditing,

    // Utilidades
    getContinuousSponsors,
    getDefaultSponsor,
  };
};